import React, { useEffect, useState, useRef } from "react";
import { useVoucherStatus } from "../../hooks/useVoucherStatus";
import { useSignedVoucher } from "../../hooks/useSignedVoucher";
import { Button } from "../../../../../button";
import { Loader } from "../../../../../loader";
import { bridge } from "../../../../../../utils/bridge";

export function Reward({
    reward,
    donationId,
    trackButtonClick,
    getLocalization,
}: {
    reward: string;
    donationId: string;
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
    getLocalization: (id: string) => string;
}) {
    const { voucher, loading, error } = useSignedVoucher({ donationId });

    const {
        status,
        loading: loadingStatus,
        error: errorStatus,
        checkStatus,
    } = useVoucherStatus({ voucher });

    const [claiming, setClaiming] = useState(false);
    const [claimError, setClaimError] = useState<string | null>(null);
    const [hasAnimated, setHasAnimated] = useState(false);
    const rewardRef = useRef<HTMLDivElement>(null);
    const hasClickedClaimRef = useRef(false);

    const createItemFlowAnimation = React.useCallback(() => {
        if (!rewardRef.current) return;

        const rewardRect = rewardRef.current.getBoundingClientRect();
        const count = parseInt(reward.split(" ")[0] || "5", 10);

        for (let i = 0; i < count; i++) {
            const item = document.createElement("div");
            item.className = `reward-flow-item reward-${rewardNameSlug}`;

            item.style.left = `${rewardRect.left + rewardRect.width / 2}px`;
            item.style.top = `${rewardRect.top + rewardRect.height / 2}px`;

            item.style.setProperty(
                "--offset-x",
                `${(Math.random() - 0.5) * 40}px`,
            );
            item.style.setProperty(
                "--offset-y",
                `${(Math.random() - 0.5) * 40}px`,
            );

            item.style.animationDelay = `${i * 0.005}s`;

            document
                .getElementsByClassName("modal-overlay")[0]
                ?.appendChild(item);

            setTimeout(
                () => {
                    item.remove();
                },
                700 + i * 5,
            );
        }
    }, [reward]);

    const handleClaim = async () => {
        setClaiming(true);
        setClaimError(null);
        hasClickedClaimRef.current = true;

        try {
            const b = await bridge();

            if (!voucher) {
                throw new Error("Voucher not available");
            }

            trackButtonClick("velvetGlitchClaimReward", undefined, {
                rewardId: donationId,
                rewardName: reward,
            });

            await b.claimVoucherQuiet(voucher);

            if (reward === "1 Chaos Drop") {
                b.close();
            }

            // Wait briefly for claim to process
            await new Promise((resolve) => setTimeout(resolve, 500));

            await checkStatus();
        } catch (err) {
            console.error("Failed to claim reward:", err);
            setClaimError("Failed to claim reward. Please try again.");
        }
    };

    useEffect(() => {
        setClaiming((prev) => {
            if (prev) return false;
            return prev;
        });

        // Animate only on the first transition to claimed, and only when the
        // user clicked claim (not on an already-redeemed voucher loaded fresh)
        if (
            status === "VOUCHER_REDEEMED" &&
            !hasAnimated &&
            hasClickedClaimRef.current &&
            rewardRef.current
        ) {
            setHasAnimated(true);
            createItemFlowAnimation();
        }
    }, [status, hasAnimated, createItemFlowAnimation]);

    const statusClass = (() => {
        switch (status) {
            case "VOUCHER_REDEEMED":
                return "claimed";
            case "VOUCHER_NOT_REDEEMED":
                return "claimable";
            default:
                return "notClaimable";
        }
    })();

    const rewardName = reward.split(" ").slice(1).join(" ");
    const rewardNameSlug = rewardName.toLowerCase().replace(/\s+/g, "-");

    const getButtonContent = () => {
        if (loading || loadingStatus) {
            return <Loader light />;
        }
        if (error || errorStatus) {
            return "Error";
        }
        if (claimError) {
            return "Error";
        }
        if (claiming) {
            return <Loader light />;
        }
        if (status === "VOUCHER_REDEEMED") {
            return getLocalization("globalText.langClaimed");
        }
        return getLocalization("globalText.langClaim");
    };

    return (
        <div
            ref={rewardRef}
            className={`reward reward-${rewardNameSlug} ${statusClass}`}
        >
            <div className="reward-image" />
            <Button onClick={handleClaim} variant="twitch">
                <span className="buttonTextWrapper">{getButtonContent()}</span>
            </Button>
        </div>
    );
}
